import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { http, currency, API, STATUS_ORDER, STATUS_CONFIG } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";
import { generateOrderPDF } from "../lib/orderPdf";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Loader2, Building2, Check, ArrowRight, Download, Info, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const showTransfer = params.get("tab") === "transferencia";

  const load = React.useCallback(() => {
    http.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const payNow = async () => {
    const { data } = await http.post("/payments/checkout", {
      order_id: order.order_id,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  };

  const shareOnWhatsApp = () => {
    if (!order) return;
    const brand = settings?.brand_name || "Printlab";
    const url = `${window.location.origin}/pedido/${order.order_id}`;
    const deposit = order.deposit_amount ?? (order.total * 0.5);
    const lines = [
      `*Cotización ${brand}*`,
      `Pedido: *${order.order_id.toUpperCase()}*`,
      `Total: *${currency(order.total)}*`,
      order.payment_status !== "paid"
        ? `Anticipo 50%: *${currency(deposit)}*`
        : `Pago: *Pagado ✓*`,
      order.delivery_method === "sucursal"
        ? `Entrega: Recolección en sucursal`
        : `Entrega estimada: ${order.delivery_estimate || "por confirmar"}`,
      ``,
      `Ver detalle y descargar PDF:`,
      url,
    ];
    const text = lines.filter((l) => l !== null && l !== undefined).join("\n");
    // No phone number → WhatsApp lets the user pick a contact from their list
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp con la cotización");
  };

  if (loading) {
    return <div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-neutral-400" /></div>;
  }
  if (!order) {
    return <div className="py-24 text-center">Pedido no encontrado</div>;
  }

  const currentIdx = STATUS_ORDER.indexOf(order.status);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="order-detail-page">
      <Link to="/perfil" className="text-xs font-mono uppercase text-neutral-500 hover:text-black">← Mis pedidos</Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500">Pedido</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-1">{order.order_id}</h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <StatusBadge status={order.status} />
            <span className="text-xs font-mono text-neutral-500">
              Pago: {order.payment_status === "paid" ? "Pagado" : "Pendiente"} · {order.payment_method}
            </span>
          </div>
        </div>
        <div className="text-right space-y-2">
          <div>
            <div className="text-xs font-mono uppercase text-neutral-500">Total</div>
            <div className="font-display text-4xl">{currency(order.total)}</div>
          </div>
          <Button
            variant="outline"
            className="rounded-none border-black hover:bg-black hover:text-white"
            onClick={async () => { try { await generateOrderPDF(order, settings); } catch { /* noop */ } }}
            data-testid="order-download-pdf"
          >
            <Download className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
          <Button
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white ml-2"
            onClick={shareOnWhatsApp}
            data-testid="order-share-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> Enviar cotización por WhatsApp
          </Button>
        </div>
      </div>

      {/* Deposit notice */}
      {order.payment_status !== "paid" && (
        <div className="mt-6 border-2 border-[#FF3B30] bg-red-50 p-4 md:p-5 flex items-start gap-3" data-testid="deposit-notice">
          <Info className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-lg md:text-xl tracking-tight">
              Anticipo del 50% para iniciar tu pedido
            </div>
            <p className="text-sm text-neutral-700 mt-1">
              Para comenzar la producción es necesario cubrir el 50% del total. El resto lo pagas al confirmar la producción o al momento de la entrega.
            </p>
            <div className="mt-3 flex items-baseline gap-4 flex-wrap">
              <div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">A pagar hoy (50%)</div>
                <div className="font-display text-3xl text-[#FF3B30]" data-testid="deposit-amount">
                  {currency(order.deposit_amount ?? order.total * 0.5)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">Resto al entregar</div>
                <div className="font-display text-2xl text-neutral-700">
                  {currency(order.total - (order.deposit_amount ?? order.total * 0.5))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline */}
          <div className="brutal-card p-6">
            <h2 className="font-display text-xl tracking-tight mb-4">Seguimiento del pedido</h2>
            <div className="relative pl-6">
              <div className="timeline-line" />
              {STATUS_ORDER.map((s, i) => {
                const done = i <= currentIdx;
                const current = i === currentIdx;
                const cfg = STATUS_CONFIG[s];
                return (
                  <div key={s} className="relative py-2.5" data-testid={`timeline-${s}`}>
                    <div
                      className="absolute -left-6 top-3 w-6 h-6 rounded-full border-2 flex items-center justify-center"
                      style={{
                        borderColor: done ? cfg.color : "#E5E5E5",
                        background: done ? cfg.color : "#FFFFFF",
                      }}
                    >
                      {done && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className={`font-medium text-sm ${current ? "text-black" : done ? "text-neutral-600" : "text-neutral-400"}`}>
                      {cfg.label}
                    </div>
                    {current && <div className="text-xs text-neutral-500 mt-0.5">Estado actual</div>}
                  </div>
                );
              })}
            </div>
            {order.history && order.history.length > 0 && (
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <div className="text-xs font-mono uppercase text-neutral-500 mb-3">Historial</div>
                <ul className="space-y-2">
                  {[...order.history].reverse().map((h, i) => (
                    <li key={i} className="text-xs flex gap-3">
                      <span className="font-mono text-neutral-500 flex-shrink-0">
                        {new Date(h.at).toLocaleString("es-MX", { hour12: false })}
                      </span>
                      <span className="text-neutral-700">{h.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="brutal-card p-6">
            <h2 className="font-display text-xl tracking-tight mb-4">Artículos</h2>
            <div className="space-y-4">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-4 border-b border-neutral-200 last:border-0 pb-4 last:pb-0">
                  <div className="w-20 h-20 bg-neutral-100 flex-shrink-0 relative overflow-hidden">
                    <img src={it.product_image} alt={it.product_name} className="w-full h-full object-cover" />
                    {it.design_url && (
                      <div
                        className="absolute pointer-events-none"
                        style={{
                          top: `${it.design_position?.top ?? 30}%`,
                          left: `${it.design_position?.left ?? 30}%`,
                          width: `${it.design_position?.width ?? 40}%`,
                          height: `${it.design_position?.height ?? 40}%`,
                          transform: `rotate(${it.design_position?.rotation ?? 0}deg)`,
                          transformOrigin: "center",
                        }}
                      >
                        <img src={`${API}/files/${it.design_url}`} alt="d" className="w-full h-full object-contain" style={{ mixBlendMode: "multiply" }} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs font-mono text-neutral-500 mt-1">
                      {it.color_label && `Color: ${it.color_label} · `}
                      {it.size_label && `Talla: ${it.size_label} · `}
                      Cant: {it.quantity}
                    </div>
                    {it.printing_method_label && (
                      <div className="text-xs font-mono text-neutral-600 mt-1">Impresión: {it.printing_method_label}</div>
                    )}
                    {it.design_filename && <div className="text-xs text-neutral-500 mt-1">Diseño: {it.design_filename}</div>}
                    {it.notes && <div className="text-xs italic text-neutral-500 mt-1">"{it.notes}"</div>}
                  </div>
                  <div className="font-display text-lg">{currency(it.line_total)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Payment CTA */}
          {order.payment_status !== "paid" && order.payment_method === "stripe" && (
            <div className="brutal-card p-5 bg-[#FF3B30] text-white">
              <div className="text-xs font-mono uppercase opacity-80">Pago pendiente</div>
              <div className="font-display text-2xl mt-1 tracking-tight">Completa tu pago</div>
              <Button onClick={payNow} className="brutal-btn w-full mt-4 bg-white text-black hover:bg-black hover:text-white h-11" data-testid="order-pay-btn">
                Pagar {currency(order.total)} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {order.payment_method === "transferencia" && order.payment_status !== "paid" && (
            <div className="brutal-card p-6" data-testid="order-transfer-instructions">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                <span className="font-display text-lg tracking-tight">Pago por transferencia</span>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-500">Banco</div>
                  <div className="font-medium">{settings.bank_name}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-500">Beneficiario</div>
                  <div className="font-medium">{settings.bank_beneficiary}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-500">CLABE</div>
                  <div className="font-mono">{settings.bank_clabe}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-500">Referencia</div>
                  <div className="font-mono text-[#FF3B30] font-bold">{order.order_id.toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-neutral-500">Monto (anticipo 50%)</div>
                  <div className="font-display text-xl text-[#FF3B30]">{currency(order.deposit_amount ?? order.total * 0.5)}</div>
                  <div className="text-[10px] font-mono text-neutral-400 mt-1">Total del pedido: {currency(order.total)}</div>
                </div>
                <p className="text-xs text-neutral-500 pt-2 border-t border-neutral-200">
                  Envíanos el comprobante a <span className="font-mono">{settings.bank_email}</span> para confirmar el pago y comenzar la producción.
                </p>
              </div>
            </div>
          )}

          {/* Delivery */}
          <div className="brutal-card p-5">
            <div className="text-xs font-mono uppercase text-neutral-500">Entrega estimada</div>
            <div className="font-display text-2xl mt-1">{order.delivery_estimate}</div>
            <div className="text-xs text-neutral-500 mt-3 pt-3 border-t border-neutral-200 font-mono uppercase">Dirección</div>
            <div className="text-sm mt-1">{order.shipping.name}</div>
            <div className="text-xs text-neutral-600">{order.shipping.address}</div>
            <div className="text-xs text-neutral-600">{order.shipping.city}, {order.shipping.state} {order.shipping.zip}</div>
            <div className="text-xs text-neutral-600 mt-1 font-mono">{order.shipping.phone}</div>
          </div>

          {/* Summary */}
          <div className="brutal-card p-5">
            <div className="text-xs font-mono uppercase text-neutral-500">Resumen</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{currency(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>Envío</span><span>{order.shipping_fee === 0 ? "Gratis" : currency(order.shipping_fee)}</span></div>
              <div className="flex justify-between font-display text-xl pt-2 border-t border-neutral-200"><span>Total</span><span>{currency(order.total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
